import { SetStateAction, useEffect, useMemo, useState } from "react";
import { Language, PasswordFlow } from "helpers";
import { AuthPasswordReset } from "./components/AuthPasswordReset";
import { AuthPasswordRecover } from "./components/AuthPasswordRecover";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { setCurrentLanguage } from "services";

export const AuthPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n: i18nInstance } = useTranslation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const routePath = location.pathname;

  const initialEmail = searchParams.get("email") ?? "";
  const initialCode = searchParams.get("code") ?? "";
  const initialLanguage = searchParams.get("lang")?.toUpperCase() ?? "EN";
  const [email, setEmail] = useState<string>(initialEmail);
  const [flow, setFlow] = useState<PasswordFlow>(() => (routePath.includes("/reset-password") ? "recover" : "reset"));

  useEffect(() => {
    const currentEmail = searchParams.get("email") ?? "";
    setEmail(currentEmail);
    setFlow(routePath.includes("/reset-password") ? "recover" : "reset");
  }, [routePath, searchParams]);

  useEffect(() => {
    if (routePath.includes("/reset-password") && !searchParams.get("email")) {
      navigate(`/forgot-password?lang=${i18nInstance.language.toLowerCase()}`, { replace: true });
    }
  }, [routePath, searchParams, navigate, i18nInstance]);

  useEffect(() => {
    const validLanguages = ["EN", "MK", "AL"] as const;
    const lang = validLanguages.includes(initialLanguage as any) ? initialLanguage : "EN";

    if (lang !== i18nInstance.language) {
      dispatch(setCurrentLanguage(lang as Language));

      i18nInstance.changeLanguage(lang).catch((error) => {
        console.error("Failed to change i18next language:", error);
      });

      localStorage.setItem("i18nextLng", lang);
    }
  }, [initialLanguage, dispatch, i18nInstance]);

  const onEmailSent = (value: SetStateAction<string>) => {
    const newEmail = typeof value === "function" ? (value as (prev: string) => string)(email) : (value as string);
    setEmail(newEmail);
    const langParam = initialLanguage ? `&lang=${initialLanguage.toLowerCase()}` : "";
    navigate(`/reset-password?email=${encodeURIComponent(newEmail)}${langParam}`, { replace: true });
  };

  return (
    <div className="text-center">
      {flow === "reset" && <AuthPasswordReset setEmail={onEmailSent} />}
      {flow === "recover" && <AuthPasswordRecover email={email} code={initialCode} />}
    </div>
  );
};
